import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import logoParticlePoints from '../data/logoParticlePoints';

/**
 * A real WebGL / Three.js point-cloud of the PlatypusBox mark.
 *
 * - ~3,000 particles (dense fill) assemble from a scattered 3D cloud into
 *   the logo shape, recolored into the site's cream / accent-green / gold
 *   palette rather than the raw sampled pixel colors.
 * - The whole mark gently sways in true 3D, bounded to a small angle so it
 *   always reads clearly.
 * - Particles spring away from the cursor when it passes near them and
 *   glow hot while displaced, then ease smoothly back into the exact logo
 *   shape once the cursor moves on - distort, then reform.
 * - Real UnrealBloomPass post-processing gives everything a genuine glow.
 * - A faint static starfield sits behind it for depth.
 */

const CREAM = new THREE.Color('#f5e6d3');
const GOLD = new THREE.Color('#e3a857');
const HOT = new THREE.Color('#ffffff');

// Soft circular sprite so points render as glowing dots instead of hard squares
function makeDiscTexture() {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createRadialGradient(
    size / 2, size / 2, 0,
    size / 2, size / 2, size / 2
  );
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.4, 'rgba(255,255,255,0.9)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

// Recolor a sampled logo pixel into the site's palette: warm (originally
// orange/brown platypus) pixels become gold, everything else becomes cream.
// Classify using the RAW sRGB hex components (not a THREE.Color, which
// auto-converts to linear space and can nudge near-white pixels over the
// threshold since the gamma curve stretches small differences near white).
function themeColorFor(hex, out) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const isWarm = r - b > 0.2;
  out.copy(isWarm ? GOLD : CREAM);
  return out;
}

const ParticleLogo3D = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    let width = mount.clientWidth;
    let height = mount.clientHeight;

    // ---- Scene / camera / renderer -------------------------------------
    const scene = new THREE.Scene();
    const bgColor = new THREE.Color('#061a10');
    scene.background = bgColor;

    const camera = new THREE.PerspectiveCamera(45, width / height, 1, 3000);
    camera.position.set(0, 0, 620);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(width, height);
    mount.appendChild(renderer.domElement);

    // Real bloom post-processing for a genuine glow on bright particles
    const composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(width, height),
      0.85, // strength
      0.55, // radius
      0.12  // threshold
    );
    composer.addPass(bloomPass);

    const discTexture = makeDiscTexture();

    // ---- Logo particle cloud --------------------------------------------
    const isSmall = width < 640;

    const fovRad = THREE.MathUtils.degToRad(camera.fov);
    const worldHeightAtZ0 = 2 * camera.position.z * Math.tan(fovRad / 2);
    const pxToWorld = worldHeightAtZ0 / height;

    const logoScale = (isSmall ? Math.min(width * 0.85, height * 0.55) : Math.min(width * 0.52, height * 0.85)) * pxToWorld;
    const groupOffsetX = 0; // always centered - the logo is the only thing on screen

    const count = logoParticlePoints.length;
    const basePositions = new Float32Array(count * 3);
    const startPositions = new Float32Array(count * 3);
    const currentPositions = new Float32Array(count * 3);
    const baseColors = new Float32Array(count * 3);
    const currentColors = new Float32Array(count * 3);
    const delays = new Float32Array(count);
    const twinkles = new Float32Array(count);
    const dispX = new Float32Array(count);
    const dispY = new Float32Array(count);
    const heat = new Float32Array(count);

    const tmpColor = new THREE.Color();

    for (let i = 0; i < count; i++) {
      const [nx, ny, hex] = logoParticlePoints[i];
      const bx = nx * logoScale;
      const by = -ny * logoScale;
      const bz = (Math.random() - 0.5) * logoScale * 0.12;

      basePositions[i * 3] = bx;
      basePositions[i * 3 + 1] = by;
      basePositions[i * 3 + 2] = bz;

      const radius = logoScale * (0.9 + Math.random() * 0.6);
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(Math.random() * 2 - 1);
      startPositions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      startPositions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      startPositions[i * 3 + 2] = radius * Math.cos(phi);

      currentPositions[i * 3] = startPositions[i * 3];
      currentPositions[i * 3 + 1] = startPositions[i * 3 + 1];
      currentPositions[i * 3 + 2] = startPositions[i * 3 + 2];

      themeColorFor(hex, tmpColor);
      baseColors[i * 3] = tmpColor.r;
      baseColors[i * 3 + 1] = tmpColor.g;
      baseColors[i * 3 + 2] = tmpColor.b;
      currentColors[i * 3] = tmpColor.r;
      currentColors[i * 3 + 1] = tmpColor.g;
      currentColors[i * 3 + 2] = tmpColor.b;

      delays[i] = (i % 80) * 9 + Math.random() * 350;
      twinkles[i] = Math.random() * Math.PI * 2;
    }

    const geometry = new THREE.BufferGeometry();
    const positionAttr = new THREE.BufferAttribute(currentPositions, 3);
    positionAttr.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('position', positionAttr);
    const colorAttr = new THREE.BufferAttribute(currentColors, 3);
    colorAttr.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('color', colorAttr);

    const material = new THREE.PointsMaterial({
      size: isSmall ? 3.4 : 3.1,
      map: discTexture,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });

    const points = new THREE.Points(geometry, material);
    const group = new THREE.Group();
    group.add(points);
    group.position.x = groupOffsetX;
    scene.add(group);

    // ---- Faint static starfield for depth --------------------------------
    const starCount = isSmall ? 90 : 200;
    const starPositions = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount; i++) {
      starPositions[i * 3] = (Math.random() - 0.5) * width * 2.2;
      starPositions[i * 3 + 1] = (Math.random() - 0.5) * height * 2.2;
      starPositions[i * 3 + 2] = -300 - Math.random() * 500;
    }
    const starGeometry = new THREE.BufferGeometry();
    starGeometry.setAttribute('position', new THREE.BufferAttribute(starPositions, 3));
    const starMaterial = new THREE.PointsMaterial({
      size: 1.6,
      map: discTexture,
      color: 0xf5e6d3,
      transparent: true,
      opacity: 0.3,
      depthWrite: false,
      sizeAttenuation: true,
    });
    const stars = new THREE.Points(starGeometry, starMaterial);
    scene.add(stars);

    // ---- Mouse tracking + raycasting for the distort effect --------------
    const raycaster = new THREE.Raycaster();
    const ndcMouse = new THREE.Vector2(-9, -9);
    const distortPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    const worldPoint = new THREE.Vector3();
    const localMouse = new THREE.Vector3();
    let mouseActive = false;

    const handlePointerMove = (clientX, clientY) => {
      const rect = mount.getBoundingClientRect();
      ndcMouse.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      ndcMouse.y = -((clientY - rect.top) / rect.height) * 2 + 1;
      mouseActive = true;
    };
    const onMouseMove = (e) => handlePointerMove(e.clientX, e.clientY);
    const onTouchMove = (e) => {
      if (e.touches[0]) handlePointerMove(e.touches[0].clientX, e.touches[0].clientY);
    };
    const onLeave = () => {
      mouseActive = false;
    };

    mount.addEventListener('mousemove', onMouseMove);
    mount.addEventListener('mouseleave', onLeave);
    mount.addEventListener('touchmove', onTouchMove, { passive: true });
    mount.addEventListener('touchend', onLeave);

    // ---- Resize -----------------------------------------------------------
    const handleResize = () => {
      width = mount.clientWidth;
      height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
      composer.setSize(width, height);
      bloomPass.setSize(width, height);
    };
    window.addEventListener('resize', handleResize);

    // ---- Animation loop -----------------------------------------------------
    const ASSEMBLE_DURATION = 2000;
    const ROTATION_MAX = THREE.MathUtils.degToRad(24);
    const ROTATION_PERIOD = 9000;
    const REPEL_RADIUS = logoScale * 0.22;
    const REPEL_STRENGTH = logoScale * 0.16;
    // Rates are "per second" and converted to a frame-independent lerp
    // factor below, so the distort/reform feel is identical on a fast
    // gaming GPU and a slow, software-rendered fallback alike.
    const SPRING_OUT_RATE = 14; // flee the cursor quickly
    const SPRING_BACK_RATE = 5.5; // ease back into the logo shape
    const finalColor = new THREE.Color();
    let lastFrameTime = performance.now();

    let rafId;
    const startTime = performance.now();

    const animate = () => {
      const now = performance.now();
      const elapsed = now - startTime;
      const dt = Math.min(0.1, (now - lastFrameTime) / 1000); // seconds, capped
      lastFrameTime = now;

      if (!prefersReducedMotion) {
        group.rotation.y = Math.sin((elapsed / ROTATION_PERIOD) * Math.PI * 2) * ROTATION_MAX;
      }

      if (mouseActive) {
        raycaster.setFromCamera(ndcMouse, camera);
        if (raycaster.ray.intersectPlane(distortPlane, worldPoint)) {
          localMouse.copy(worldPoint);
          group.worldToLocal(localMouse);
        }
      }

      const posArray = geometry.attributes.position.array;
      const colArray = geometry.attributes.color.array;

      for (let i = 0; i < count; i++) {
        const localT = prefersReducedMotion
          ? 1
          : Math.max(0, Math.min(1, (elapsed - delays[i]) / ASSEMBLE_DURATION));
        const eased = 1 - Math.pow(1 - localT, 3);

        const bx = basePositions[i * 3];
        const by = basePositions[i * 3 + 1];
        const bz = basePositions[i * 3 + 2];

        const sx = startPositions[i * 3];
        const sy = startPositions[i * 3 + 1];
        const sz = startPositions[i * 3 + 2];

        const targetX = sx + (bx - sx) * eased;
        const targetY = sy + (by - sy) * eased;
        const targetZ = sz + (bz - sz) * eased;

        // mouse distortion (only once mostly assembled) - push away, then
        // spring back into the exact logo position once the cursor leaves.
        let wantX = 0;
        let wantY = 0;
        let wantHeat = 0;
        if (mouseActive && eased > 0.4) {
          const dx = targetX - localMouse.x;
          const dy = targetY - localMouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < REPEL_RADIUS && dist > 0.001) {
            const force = Math.pow(1 - dist / REPEL_RADIUS, 1.6) * REPEL_STRENGTH;
            wantX = (dx / dist) * force;
            wantY = (dy / dist) * force;
            wantHeat = 1 - dist / REPEL_RADIUS;
          }
        }
        const goingOut = Math.abs(wantX) + Math.abs(wantY) > Math.abs(dispX[i]) + Math.abs(dispY[i]);
        const rate = goingOut ? SPRING_OUT_RATE : SPRING_BACK_RATE;
        const springFactor = 1 - Math.exp(-rate * dt);
        dispX[i] += (wantX - dispX[i]) * springFactor;
        dispY[i] += (wantY - dispY[i]) * springFactor;
        const heatRate = wantHeat > heat[i] ? SPRING_OUT_RATE : SPRING_BACK_RATE;
        heat[i] += (wantHeat - heat[i]) * (1 - Math.exp(-heatRate * dt));

        const floatX = Math.sin(now / 1400 + twinkles[i]) * 1.1 * eased;
        const floatY = Math.cos(now / 1600 + twinkles[i]) * 1.1 * eased;

        posArray[i * 3] = targetX + dispX[i] + floatX;
        posArray[i * 3 + 1] = targetY + dispY[i] + floatY;
        posArray[i * 3 + 2] = targetZ;

        // twinkle brightness + glow hot while displaced by the cursor
        const twinkleFactor = 0.78 + Math.sin(now / 850 + twinkles[i]) * 0.22;
        finalColor.setRGB(
          baseColors[i * 3] * twinkleFactor,
          baseColors[i * 3 + 1] * twinkleFactor,
          baseColors[i * 3 + 2] * twinkleFactor
        );
        if (heat[i] > 0.01) {
          finalColor.lerp(HOT, Math.min(1, heat[i] * 1.4));
        }
        colArray[i * 3] = finalColor.r;
        colArray[i * 3 + 1] = finalColor.g;
        colArray[i * 3 + 2] = finalColor.b;
      }
      geometry.attributes.position.needsUpdate = true;
      geometry.attributes.color.needsUpdate = true;

      composer.render();
      rafId = requestAnimationFrame(animate);
    };

    rafId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
      mount.removeEventListener('mousemove', onMouseMove);
      mount.removeEventListener('mouseleave', onLeave);
      mount.removeEventListener('touchmove', onTouchMove);
      mount.removeEventListener('touchend', onLeave);
      geometry.dispose();
      material.dispose();
      starGeometry.dispose();
      starMaterial.dispose();
      discTexture.dispose();
      composer.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return <div ref={mountRef} className="absolute inset-0 w-full h-full" aria-hidden="true" />;
};

export default ParticleLogo3D;
